import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import orderApi from "@api/order";
import tradesApi from "@api/trades";
import Client from "@api/client";
import { StatsContext } from "@context/statsContext";
import { useMarkets } from "@context/marketsContext";
import RangerClient from "@lib/ranger/rangerClient";
import {
  applyDepthIncrement,
  depthToShahooBook,
  snapshotToDepth,
} from "@lib/ranger/depth";
import {
  isRangerEnabled,
  isRangerIncrementalOrderBook,
  marketKlineStream,
  marketPublicStreams,
  periodMinutesToString,
  resolveMarketId,
} from "@lib/ranger/helpers";
import { mapRangerTickersToStatMap } from "@lib/ranger/mapTickers";
import { setRangerKlineSubscribe } from "@features/Market/MainComponents/TVChartContainer/datafeeds/streaming";

const RangerContext = createContext(null);

const emptyDepth = {
  asks: [],
  bids: [],
  sequence: null,
  loading: false,
  sells: [],
  buys: [],
  is_fetch: false,
};

const PRIVATE_RECONNECT_MS = 1500;
const PRIVATE_RECONNECT_MAX_MS = 30000;
const PRIVATE_CREDS_TTL_MS = 10 * 60 * 1000;

function mapWsTrade(raw) {
  const side = String(raw.taker_type || raw.side || "").toLowerCase();
  return {
    id: raw.tid || raw.id,
    unitPrice: parseFloat(raw.price) || 0,
    amount: parseFloat(raw.amount) || 0,
    total: (parseFloat(raw.price) || 0) * (parseFloat(raw.amount) || 0),
    time: Number(raw.date) || Math.floor(Date.now() / 1000),
    side: side === "buy" || side === "BUY" ? "buy" : "sell",
    market: raw.market,
  };
}

function toShahooMarketKey(routeOrKey, marketId) {
  const key = String(routeOrKey || "")
    .replace(/-/g, "_")
    .toUpperCase();
  if (key.includes("_")) return key;
  if (!marketId) return key;
  const m = String(marketId).match(/^([a-z0-9]+?)(cad|irt|usdt)$/i);
  if (m) return `${m[1].toUpperCase()}_${m[2].toUpperCase()}`;
  return key;
}

export function RangerProvider({ children }) {
  const enabled = isRangerEnabled();
  const { statsDispatch, statsState } = useContext(StatsContext);
  const { getMarketByRoute } = useMarkets();

  const clientRef = useRef(null);
  const marketStreamsRef = useRef([]);
  const marketRefCountRef = useRef(0);
  const marketKeyRef = useRef(null);
  const klineStreamsRef = useRef([]);
  const klineHandlersRef = useRef(new Map());
  const currentMarketIdRef = useRef(null);
  const resyncingRef = useRef(false);
  const privateWantedRef = useRef(false);
  const privateReconnectTimerRef = useRef(null);
  const privateReconnectAttemptRef = useRef(0);
  const openPrivateSocketRef = useRef(null);
  const privateCredsRef = useRef(null); // { privateWsUrl, token, streams, at }
  const privateHandlersRef = useRef({
    order: new Set(),
    trade: new Set(),
    balances: new Set(),
  });

  const [connection, setConnection] = useState({
    connected: false,
    connecting: false,
    withAuth: false,
    subscriptions: [],
  });
  const [currentMarketId, setCurrentMarketId] = useState(null);
  const [depth, setDepth] = useState(emptyDepth);
  const [trades, setTrades] = useState([]);
  const [privateReady, setPrivateReady] = useState(false);

  const statsRef = useRef(statsState?.stats || {});
  useEffect(() => {
    statsRef.current = statsState?.stats || {};
  }, [statsState?.stats]);

  const prefetchDepth = useCallback(async (marketId, marketKey) => {
    setDepth((prev) => ({ ...prev, loading: true, sequence: null }));
    try {
      const shahooKey = toShahooMarketKey(marketKey, marketId);
      const res = await orderApi.getMarketOrderBook1({
        params: { market: shahooKey },
      });
      const sells = res?.content?.sells || [];
      const buys = res?.content?.buys || [];
      const asks = sells.map((r) => [String(r.unitPrice), String(r.size)]);
      const bids = buys.map((r) => [String(r.unitPrice), String(r.size)]);
      setDepth({
        asks,
        bids,
        sequence: null,
        loading: false,
        sells,
        buys,
        is_fetch: true,
      });
    } catch (e) {
      console.warn("[ranger] depth prefetch failed", e);
      setDepth((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const prefetchTrades = useCallback(async (marketKey) => {
    try {
      const res = await tradesApi.getRecentMarkets({
        params: { market: marketKey },
      });
      setTrades(res?.content || []);
    } catch (e) {
      console.warn("[ranger] trades prefetch failed", e);
    }
  }, []);

  const resyncOrderBook = useCallback(
    async (reason) => {
      const marketId = currentMarketIdRef.current;
      const marketKey = marketKeyRef.current;
      if (!marketId || resyncingRef.current) return;
      resyncingRef.current = true;
      console.warn(`[ranger] OB resync: ${reason}`);
      try {
        await prefetchDepth(marketId, marketKey);
        clientRef.current?.reconnect();
      } finally {
        resyncingRef.current = false;
      }
    },
    [prefetchDepth]
  );

  const clearPrivateReconnect = useCallback(() => {
    if (privateReconnectTimerRef.current) {
      clearTimeout(privateReconnectTimerRef.current);
      privateReconnectTimerRef.current = null;
    }
  }, []);

  const disconnectPrivate = useCallback(() => {
    privateWantedRef.current = false;
    privateReconnectAttemptRef.current = 0;
    privateCredsRef.current = null;
    clearPrivateReconnect();
    const priv = clientRef.current?._privateWs;
    if (priv) {
      try {
        priv.onclose = null;
        priv.close();
      } catch (_) {
        /* ignore */
      }
      if (clientRef.current) clientRef.current._privateWs = null;
    }
    setPrivateReady(false);
  }, [clearPrivateReconnect]);

  const schedulePrivateReconnect = useCallback(() => {
    if (!privateWantedRef.current) return;
    clearPrivateReconnect();
    const attempt = privateReconnectAttemptRef.current;
    const delay = Math.min(
      PRIVATE_RECONNECT_MS * Math.pow(2, attempt),
      PRIVATE_RECONNECT_MAX_MS
    );
    privateReconnectAttemptRef.current = attempt + 1;
    privateReconnectTimerRef.current = setTimeout(() => {
      openPrivateSocketRef.current?.();
    }, delay);
  }, [clearPrivateReconnect]);

  const fetchPrivateCredentials = useCallback(async () => {
    const cached = privateCredsRef.current;
    if (
      cached?.token &&
      cached?.privateWsUrl &&
      Date.now() - cached.at < PRIVATE_CREDS_TTL_MS
    ) {
      return cached;
    }
    const creds = await Client.getWithoutLoading("/api/1/ranger/credentials");
    const privateWsUrl = creds?.content?.privateWsUrl;
    const token = creds?.content?.token;
    const streams = creds?.content?.streams || ["order", "trade"];
    if (!privateWsUrl || !token) return null;
    const next = { privateWsUrl, token, streams, at: Date.now() };
    privateCredsRef.current = next;
    return next;
  }, []);

  const openPrivateSocket = useCallback(async () => {
    if (!enabled || !privateWantedRef.current) return false;
    const existing = clientRef.current?._privateWs;
    if (existing) {
      try {
        existing.onclose = null;
        existing.close();
      } catch (_) {
        /* ignore */
      }
      if (clientRef.current) clientRef.current._privateWs = null;
    }

    try {
      const creds = await fetchPrivateCredentials();
      if (!creds || !privateWantedRef.current) {
        setPrivateReady(false);
        schedulePrivateReconnect();
        return false;
      }
      const streamQ = (creds.streams || ["order", "trade"])
        .filter(Boolean)
        .sort()
        .map((s) => `stream=${encodeURIComponent(s)}`)
        .join("&");
      const sep = creds.privateWsUrl.includes("?") ? "&" : "?";
      const url = `${creds.privateWsUrl}${sep}token=${encodeURIComponent(
        creds.token
      )}&${streamQ}`;
      const priv = new WebSocket(url);
      priv.onmessage = ({ data }) => {
        try {
          const payload = JSON.parse(data);
          Object.keys(payload).forEach((k) => {
            if (k === "success") return; // subscribe ack — connection alive
            if (k === "order") {
              privateHandlersRef.current.order.forEach((fn) => fn(payload[k]));
            } else if (k === "trade") {
              privateHandlersRef.current.trade.forEach((fn) => fn(payload[k]));
            } else if (k === "balances") {
              privateHandlersRef.current.balances.forEach((fn) =>
                fn(payload[k])
              );
            }
          });
        } catch (_) {
          /* ignore */
        }
      };
      priv.onopen = () => {
        privateReconnectAttemptRef.current = 0;
        setPrivateReady(true);
      };
      priv.onerror = () => setPrivateReady(false);
      priv.onclose = () => {
        setPrivateReady(false);
        if (clientRef.current) clientRef.current._privateWs = null;
        // Force fresh credentials after auth-ish failures
        if (privateReconnectAttemptRef.current >= 2) {
          privateCredsRef.current = null;
        }
        schedulePrivateReconnect();
      };
      if (clientRef.current) clientRef.current._privateWs = priv;
      return true;
    } catch (_) {
      setPrivateReady(false);
      privateCredsRef.current = null;
      schedulePrivateReconnect();
      return false;
    }
  }, [enabled, fetchPrivateCredentials, schedulePrivateReconnect]);

  useEffect(() => {
    openPrivateSocketRef.current = openPrivateSocket;
  }, [openPrivateSocket]);

  const connectPrivate = useCallback(async () => {
    if (!enabled) return false;
    privateWantedRef.current = true;
    privateReconnectAttemptRef.current = 0;
    clearPrivateReconnect();
    return openPrivateSocket();
  }, [enabled, clearPrivateReconnect, openPrivateSocket]);

  useEffect(() => {
    if (!enabled) return undefined;

    const client = new RangerClient();
    clientRef.current = client;

    const unsubMsg = client.onMessage((routingKey, event) => {
      if (routingKey === "global.tickers") {
        const next = mapRangerTickersToStatMap(event, statsRef.current);
        statsRef.current = next;
        statsDispatch({ type: "SET_STATS", stats: next });
        return;
      }

      if (routingKey === "success") {
        if (
          event?.message === "subscribed" ||
          event?.message === "unsubscribed"
        ) {
          setConnection((c) => ({
            ...c,
            subscriptions: event.streams || c.subscriptions,
          }));
        }
        return;
      }

      const obInc = String(routingKey).match(/^([^.]+)\.ob-inc$/);
      if (obInc) {
        const mid = obInc[1];
        if (mid !== currentMarketIdRef.current) return;

        setDepth((prev) => {
          if (prev.loading || resyncingRef.current) return prev;

          if (prev.sequence == null) {
            // After REST prefetch (no sequence): accept first increment and adopt its sequence.
            const next = applyDepthIncrement(prev, event);
            const book = depthToShahooBook(next.asks, next.bids);
            return {
              ...next,
              ...book,
              is_fetch: true,
              loading: false,
              sequence:
                event.sequence != null ? Number(event.sequence) : null,
            };
          }

          if (prev.sequence + 1 !== Number(event.sequence)) {
            // Defer resync outside setState
            Promise.resolve().then(() =>
              resyncOrderBook(
                `seq ${prev.sequence} → ${event.sequence}`
              )
            );
            return { ...prev, loading: true };
          }

          const next = applyDepthIncrement(prev, event);
          const book = depthToShahooBook(next.asks, next.bids);
          return { ...next, ...book, is_fetch: true };
        });
        return;
      }

      const obSnap = String(routingKey).match(/^([^.]+)\.ob-snap$/);
      if (obSnap) {
        if (obSnap[1] !== currentMarketIdRef.current) return;
        const snap = snapshotToDepth(event);
        const book = depthToShahooBook(snap.asks, snap.bids);
        setDepth({ ...snap, ...book, is_fetch: true });
        return;
      }

      const obUpdate = String(routingKey).match(/^([^.]+)\.update$/);
      if (obUpdate) {
        if (obUpdate[1] !== currentMarketIdRef.current) return;
        const snap = snapshotToDepth(event);
        const book = depthToShahooBook(snap.asks, snap.bids);
        setDepth({ ...snap, ...book, is_fetch: true, sequence: null });
        return;
      }

      const tradesMatch = String(routingKey).match(/^([^.]+)\.trades$/);
      if (tradesMatch) {
        if (tradesMatch[1] !== currentMarketIdRef.current) return;
        const incoming = (event?.trades || []).map(mapWsTrade);
        if (!incoming.length) return;
        setTrades((prev) => {
          const merged = [...incoming, ...prev];
          const seen = new Set();
          return merged
            .filter((t) => {
              const id = t.id ?? `${t.time}-${t.unitPrice}-${t.amount}`;
              if (seen.has(id)) return false;
              seen.add(id);
              return true;
            })
            .slice(0, 50);
        });
        return;
      }

      const klineMatch = String(routingKey).match(/^([^.]+)\.kline-(.+)$/);
      if (klineMatch) {
        const marketId = klineMatch[1];
        const period = klineMatch[2];
        const key = `${marketId}|${period}`;
        const handlers = klineHandlersRef.current.get(key);
        if (handlers && handlers.size) {
          const bar = Array.isArray(event)
            ? {
                time: Number(event[0]) * 1000,
                open: Number(event[1]),
                high: Number(event[2]),
                low: Number(event[3]),
                close: Number(event[4]),
                volume: Number(event[5]) || 0,
              }
            : event;
          handlers.forEach((fn) => fn(bar));
        }
      }
    });

    const unsubConn = client.onConnectionChange(setConnection);
    client.connect({ withAuth: false, streams: ["global.tickers"] });

    return () => {
      unsubMsg();
      unsubConn();
      disconnectPrivate();
      client.disconnect();
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, statsDispatch, disconnectPrivate, resyncOrderBook]);

  useEffect(() => {
    currentMarketIdRef.current = currentMarketId;
  }, [currentMarketId]);

  const unsubscribeMarket = useCallback(() => {
    const client = clientRef.current;
    marketRefCountRef.current = Math.max(0, marketRefCountRef.current - 1);
    if (marketRefCountRef.current > 0) return;

    if (client && marketStreamsRef.current.length) {
      client.unsubscribe(marketStreamsRef.current);
      marketStreamsRef.current = [];
    }
    currentMarketIdRef.current = null;
    marketKeyRef.current = null;
    setCurrentMarketId(null);
    setDepth(emptyDepth);
    setTrades([]);
  }, []);

  const subscribeMarket = useCallback(
    async (routeOrKey) => {
      const client = clientRef.current;
      if (!enabled || !client) return null;

      const marketId = resolveMarketId(routeOrKey, getMarketByRoute);
      if (!marketId) return null;

      const marketKey = toShahooMarketKey(routeOrKey, marketId);
      marketRefCountRef.current += 1;

      // Already on this market — skip re-subscribe / re-prefetch (anti-thrash).
      if (currentMarketIdRef.current === marketId) {
        return marketId;
      }

      if (currentMarketIdRef.current && marketStreamsRef.current.length) {
        client.unsubscribe(marketStreamsRef.current);
      }

      const streams = marketPublicStreams(marketId, {
        incremental: isRangerIncrementalOrderBook(),
      });
      marketStreamsRef.current = streams;
      marketKeyRef.current = marketKey;
      currentMarketIdRef.current = marketId;
      setCurrentMarketId(marketId);
      client.subscribe(streams);

      await Promise.all([
        prefetchDepth(marketId, marketKey),
        prefetchTrades(marketKey),
      ]);

      return marketId;
    },
    [enabled, getMarketByRoute, prefetchDepth, prefetchTrades]
  );

  const subscribeKline = useCallback(
    (marketIdOrRoute, resolutionMinutes, onBar) => {
      const client = clientRef.current;
      if (!enabled || !client || !marketIdOrRoute) return () => {};

      const marketId =
        resolveMarketId(marketIdOrRoute, getMarketByRoute) ||
        String(marketIdOrRoute).toLowerCase();
      const period = periodMinutesToString(resolutionMinutes);
      const stream = marketKlineStream(marketId, period);
      const key = `${marketId}|${period}`;

      if (!klineHandlersRef.current.has(key)) {
        klineHandlersRef.current.set(key, new Set());
      }
      klineHandlersRef.current.get(key).add(onBar);

      if (!klineStreamsRef.current.includes(stream)) {
        klineStreamsRef.current.push(stream);
        client.subscribe([stream]);
      }

      return () => {
        const set = klineHandlersRef.current.get(key);
        if (set) {
          set.delete(onBar);
          if (set.size === 0) {
            klineHandlersRef.current.delete(key);
            klineStreamsRef.current = klineStreamsRef.current.filter(
              (s) => s !== stream
            );
            client.unsubscribe([stream]);
          }
        }
      };
    },
    [enabled, getMarketByRoute]
  );

  useEffect(() => {
    setRangerKlineSubscribe(subscribeKline);
    return () => setRangerKlineSubscribe(null);
  }, [subscribeKline]);

  const onPrivateEvent = useCallback((type, fn) => {
    const bucket = privateHandlersRef.current[type];
    if (!bucket) return () => {};
    bucket.add(fn);
    return () => bucket.delete(fn);
  }, []);

  // Stable API slice — does NOT include depth/trades (avoids consumer thrash).
  const api = useMemo(
    () => ({
      enabled,
      connected: connection.connected,
      connecting: connection.connecting,
      privateReady,
      currentMarketId,
      subscribeMarket,
      unsubscribeMarket,
      subscribeKline,
      connectPrivate,
      disconnectPrivate,
      onPrivateEvent,
      isFallback: !enabled || !connection.connected,
    }),
    [
      enabled,
      connection.connected,
      connection.connecting,
      privateReady,
      currentMarketId,
      subscribeMarket,
      unsubscribeMarket,
      subscribeKline,
      connectPrivate,
      disconnectPrivate,
      onPrivateEvent,
    ]
  );

  const value = useMemo(
    () => ({
      ...api,
      depth,
      trades,
    }),
    [api, depth, trades]
  );

  return (
    <RangerContext.Provider value={value}>{children}</RangerContext.Provider>
  );
}

export function useRanger() {
  const ctx = useContext(RangerContext);
  if (!ctx) {
    throw new Error("useRanger must be used within RangerProvider");
  }
  return ctx;
}

/** Safe hook when provider might be absent (optional). */
export function useRangerOptional() {
  return useContext(RangerContext);
}
